import { useState } from 'react';
import { useQuery } from 'react-query';
import { api } from '../../../services/api';
import { Panel, PanelHeader } from '../../ui/Panel';
import Button from '../../ui/Button';
import { ErrorNotice, SkeletonRows } from '../../ui/States';
import { fmt } from '../../../lib/format';
import { KIND_LABEL } from '../../../lib/events';
import { InsightEvent, InsightYear, Insights, downloadCsv, eventLabel, rate } from '../../../lib/eventInsights';
import { Block, InlineBar, dash, tEm, tHead, tNum, tRow, tTable, tTd, tTh } from './InsightCharts';
import CompareEvents from './CompareEvents';

const DIMENSIONS: [keyof Pick<InsightEvent['who'], 'byRegion' | 'byCareerStage' | 'byDiscipline'>, string][] = [
  ['byRegion', 'Region'],
  ['byCareerStage', 'Career stage'],
  ['byDiscipline', 'Discipline'],
];
const KIND_FILTER: Record<string, string> = {
  national: 'National',
  regional: 'Regional',
  pi_meeting: 'PI meetings',
  outreach: 'Outreach',
};
const filterSelect =
  'border border-line-strong rounded-sm bg-white px-3 py-2 min-h-[40px] text-[15px] leading-5 text-ink max-w-full';
const cols = 'grid grid-cols-1 xl:grid-cols-2 gap-x-16 gap-y-12';
const fullTitle = (e: InsightEvent) => `${e.year} · ${e.title}`;
const plural = (n: number, one: string, many: string) => (n === 1 ? one : `${fmt(n)} ${many}`);
const hasProfile = (y: InsightYear | undefined) =>
  Boolean(y && (DIMENSIONS.some(([k]) => y.who[k].items.length) || y.who.equity.answeredBy));

/** The KPI band as one sentence: events, attendances, named people, returning share, people who came twice. */
function summary(d: Insights) {
  const years = d.years;
  const span = years.length
    ? years.length > 1
      ? `from ${years[0].year} to ${years[years.length - 1].year}`
      : `in ${years[0].year}`
    : '';
  const attended = years.reduce((s, y) => s + y.attended, 0);
  const counted = years.reduce((s, y) => s + (y.countsOnly ?? 0), 0);
  const named = attended - counted;
  const returning = d.events.reduce((s, e) => s + e.returning, 0);
  const events = d.events.length === 1 ? 'One event' : `${fmt(d.events.length)} events`;
  if (!attended)
    return `${events} ${span} ${d.events.length === 1 ? 'is' : 'are'} on the platform; none has recorded attendance yet.`;
  const parts = [`${events} ${span} drew ${plural(attended, 'one attendance', 'attendances')}.`];
  if (named > 0) {
    parts.push(
      d.retention.people === 1
        ? 'One of them was a person we can name;'
        : `${fmt(d.retention.people)} of them were people we can name;`,
    );
    const share = rate(named ? returning / named : null);
    parts.push(
      `${share} of ${counted ? 'named attendances' : 'attendances'} were by someone who had come before, and ${plural(
        d.retention.twoPlus,
        'one person',
        'people',
      )} came twice or more.`,
    );
  }
  return parts.join(' ');
}

/** "Trends across events": the year-over-year figures at the bottom of the events list, or Compare events in place. */
export default function EventInsights() {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [kind, setKind] = useState('');
  const [dimension, setDimension] = useState<(typeof DIMENSIONS)[number][0]>('byRegion');
  const [whoYear, setWhoYear] = useState<number | null>(null);
  const [compare, setCompare] = useState(false);
  const [also, setAlso] = useState<'' | 'returning'>('');
  const q = useQuery<Insights>(
    ['event-insights', from, to, kind],
    async () =>
      (
        await api.get('/events/insights', {
          params: { from: from || undefined, to: to || undefined, kind: kind || undefined },
        })
      ).data.data,
    { keepPreviousData: true },
  );
  const d = q.data;

  if (compare && d?.events.length) {
    return (
      <Panel className="!mt-20 max-md:!mt-12">
        <CompareEvents
          insights={d}
          onBack={() => {
            setCompare(false);
            requestAnimationFrame(() => document.getElementById('trends')?.scrollIntoView({ block: 'start' }));
          }}
        />
      </Panel>
    );
  }

  const admin = Boolean(d?.moneyVisible);
  const csv = (name: string, rows: (string | number | null | undefined)[][]) =>
    admin ? [{ onClick: () => downloadCsv(name, rows) }] : undefined;
  const events = d?.events ?? [];
  const years = d?.years ?? [];
  const roleYears = years.filter((y) => y.who.byRole.some((x) => x.count));
  const roleNames = [...new Set(years.flatMap((y) => y.who.byRole.map((r) => r.value)))];
  const nationalAll = events.filter((e) => e.kind === 'national').sort((a, b) => a.year - b.year);
  const onlineMissing = nationalAll.filter(
    (e) => e.ended && e.figures.attended && e.mode === 'hybrid' && !e.figures.virtual,
  );
  const barMax = Math.max(1, ...nationalAll.flatMap((e) => [e.figures.registered, e.ended ? e.figures.attended : 0]));
  const hasBlock = events.some((e) => e.logistics.hotel.block !== null);
  const sideBySide = events.filter((e) =>
    e.kind === 'national'
      ? Boolean(
          e.program?.sessions ||
          e.logistics.expectedInPerson ||
          e.logistics.hotel.peakRooms ||
          e.logistics.hotel.block !== null,
        )
      : Boolean(e.logistics.hotel.peakRooms || e.logistics.hotel.block !== null),
  );
  const ratingOf = (e: InsightEvent) =>
    e.program?.sessionRating ?? e.evaluation.numeric.find((n) => n.mean !== null)?.mean ?? null;
  const anyRating = sideBySide.some((e) => ratingOf(e) !== null);
  const nightsEach = [
    ...new Set(sideBySide.map((e) => e.logistics.hotel.nightsPerTraveller).filter((n) => n !== null)),
  ];
  const multiNight = sideBySide.filter((e) => e.logistics.hotel.nights.length > 1);
  // Opens on the latest year with profile answers (this year's often has none until people register).
  const yearForWho =
    years.find((y) => y.year === whoYear) ?? [...years].reverse().find(hasProfile) ?? years[years.length - 1];
  const retentionMax = Math.max(1, ...(d?.retention.distribution ?? []).map((b) => b.count));

  return (
    <Panel className="!mt-20 max-md:!mt-12">
      <div id="trends" className="scroll-mt-24" />
      <PanelHeader title={<span className="text-[26px] leading-[34px]">Trends across events</span>}>
        <label className="sr-only" htmlFor="trends-from">
          From year
        </label>
        <select id="trends-from" className={filterSelect} value={from} onChange={(e) => setFrom(e.target.value)}>
          <option value="">From any year</option>
          {(d?.allYears ?? []).map((y) => (
            <option key={y} value={y}>
              From {y}
            </option>
          ))}
        </select>
        <label className="sr-only" htmlFor="trends-to">
          To year
        </label>
        <select id="trends-to" className={filterSelect} value={to} onChange={(e) => setTo(e.target.value)}>
          <option value="">To any year</option>
          {(d?.allYears ?? []).map((y) => (
            <option key={y} value={y}>
              To {y}
            </option>
          ))}
        </select>
        <label className="sr-only" htmlFor="trends-kind">
          Kind
        </label>
        <select id="trends-kind" className={filterSelect} value={kind} onChange={(e) => setKind(e.target.value)}>
          <option value="">Every kind</option>
          {Object.keys(KIND_LABEL).map((k) => (
            <option key={k} value={k}>
              {KIND_FILTER[k] ?? KIND_LABEL[k]}
            </option>
          ))}
        </select>
        <Button disabled={!d || events.length < 2} onClick={() => setCompare(true)}>
          Compare events
        </Button>
      </PanelHeader>

      {q.isLoading ? (
        <SkeletonRows rows={3} />
      ) : q.error || !d ? (
        <ErrorNotice>Could not load the trends. Reload the page to try again.</ErrorNotice>
      ) : !events.length ? (
        <p className="text-[15px] leading-[22px] text-ink-2 max-w-[72ch]">
          No in-person event in this selection yet. Past events appear here once they are on the platform (created, or
          imported with their attendance).
        </p>
      ) : (
        <>
          <p className="font-serif text-[20px] leading-[30px] text-ink max-w-[980px]">{summary(d)}</p>
          {(() => {
            const counted = years.reduce((s, y) => s + (y.countsOnly ?? 0), 0);
            const lines = [
              counted ? `${plural(counted, 'One attendance was', 'attendances were')} counted without names.` : '',
              ...d.unavailable.map((u) => u.reason),
            ].filter(Boolean);
            return lines.length ? <p className="text-small text-ink-3 mt-2 max-w-[100ch]">{lines.join(' ')}</p> : null;
          })()}

          {nationalAll.length || roleYears.length ? (
            <div className={`${cols} mt-10`}>
              {nationalAll.length ? (
                <Block
                  title="National meetings: registered and attended"
                  note={`${d.notes.registered} ${d.notes.attended} ${d.notes.mode}${
                    years.some((y) => y.countsOnly) ? ` ${d.notes.countsOnly ?? ''}` : ''
                  }`}
                  csv={csv('national-attendance', [
                    [
                      'Year',
                      'Event',
                      'Registered',
                      'Confirmed',
                      'Declined',
                      'Attended',
                      'In person',
                      'Online only',
                      'Person-days',
                    ],
                    ...nationalAll.map((e) => [
                      e.year,
                      e.title,
                      e.figures.registered,
                      e.figures.confirmed,
                      e.figures.declined,
                      e.ended ? e.figures.attended : '',
                      e.ended ? e.figures.inPerson : '',
                      e.ended ? e.figures.virtual : '',
                      e.figures.personDays,
                    ]),
                  ])}
                  footnote={
                    <>
                      Thick bar: attended. Thin bar: registered.
                      {onlineMissing.length
                        ? ` Online attendance wasn’t recorded for ${onlineMissing.map((e) => e.year).join(', ')}.`
                        : ''}
                    </>
                  }
                >
                  <table className={tTable}>
                    <thead>
                      <tr className={tHead}>
                        <th className={tTh}>Year</th>
                        <th className={`${tTh} ${tNum}`}>Registered</th>
                        <th className={`${tTh} ${tNum}`}>Attended</th>
                        <th className={`${tTh} w-[45%]`}>
                          <span className="sr-only">Bar</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {nationalAll.map((e) => {
                        const att = e.ended ? e.figures.attended : null;
                        return (
                          <tr key={e.id} className={tRow}>
                            <td className={tTd}>
                              {e.year}
                              {nationalAll.filter((x) => x.year === e.year).length > 1 ? (
                                <span className={tEm}>{e.title}</span>
                              ) : null}
                            </td>
                            <td className={`${tTd} ${tNum}`}>
                              {e.figures.registered ? fmt(e.figures.registered) : dash}
                            </td>
                            <td className={`${tTd} ${tNum}`}>
                              {att ? fmt(att) : dash}
                              {att && (e.mode === 'hybrid' || e.figures.virtual) ? (
                                <span className={tEm}>
                                  {fmt(e.figures.inPerson)} in person · {fmt(e.figures.virtual)} online
                                </span>
                              ) : null}
                            </td>
                            <td className={tTd}>
                              <InlineBar main={att} compare={e.figures.registered || null} max={barMax} />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </Block>
              ) : null}

              {roleYears.length ? (
                <Block
                  title="Who comes, by role"
                  note={d.notes.who}
                  csv={csv('who-by-role', [
                    ['Role', ...roleYears.map((y) => String(y.year))],
                    ...roleNames.map((r) => [
                      r,
                      ...roleYears.map((y) => y.who.byRole.find((x) => x.value === r)?.count ?? 0),
                    ]),
                  ])}
                  footnote="Different people per year; someone at two events in a year counts once."
                >
                  <div className="overflow-x-auto">
                    <table className={tTable}>
                      <thead>
                        <tr className={tHead}>
                          <th className={tTh}>Role</th>
                          {roleYears.map((y) => (
                            <th key={y.year} className={`${tTh} ${tNum}`}>
                              {y.year}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {roleNames.map((r) => (
                          <tr key={r} className={tRow}>
                            <td className={tTd}>{r}</td>
                            {roleYears.map((y) => (
                              <td key={y.year} className={`${tTd} ${tNum}`}>
                                {fmt(y.who.byRole.find((x) => x.value === r)?.count ?? 0)}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Block>
              ) : null}
            </div>
          ) : null}

          {sideBySide.length ? (
            <div className="mt-12">
              <Block
                title="National meetings side by side"
                note={`${d.notes.logistics}${admin ? ` ${d.notes.dietary}` : ''} ${d.notes.program} ${d.notes.evaluation}`}
                csv={
                  admin
                    ? [
                        {
                          onClick: () =>
                            downloadCsv('national-side-by-side', [
                              [
                                'Event',
                                'Kind',
                                'Expected in person',
                                'Sessions',
                                'Speakers',
                                'Speakers by affiliation',
                                'Hotel travellers',
                                'Room-nights',
                                'Nights per traveller',
                                'Peak rooms',
                                'Block',
                                'Parking',
                                'Dietary',
                                'Dietary groups',
                                'Accessibility',
                                'Travel modes',
                                'Rating',
                              ],
                              ...sideBySide.map((e) => {
                                const l = e.logistics;
                                return [
                                  fullTitle(e),
                                  KIND_LABEL[e.kind] ?? e.kind,
                                  l.expectedInPerson,
                                  e.program?.sessions ?? '',
                                  e.program?.speakers ?? '',
                                  (e.program?.speakersByAffiliation ?? [])
                                    .map((a) => `${a.value} ${a.count}`)
                                    .join('; '),
                                  l.hotel.travellers,
                                  l.hotel.roomNights,
                                  l.hotel.nightsPerTraveller,
                                  l.hotel.peakRooms,
                                  l.hotel.block,
                                  l.parking,
                                  l.dietary,
                                  (l.dietaryGroups ?? []).map((g) => `${g.value} ${g.count}`).join('; '),
                                  l.accessibility,
                                  l.travelModes.map((t) => `${t.value} ${t.count}`).join('; '),
                                  ratingOf(e),
                                ];
                              }),
                            ]),
                        },
                        {
                          label: 'Hotel by night (CSV)',
                          onClick: () =>
                            downloadCsv('hotel-by-night', [
                              ['Event', 'Night', 'Rooms', 'Block', 'Over the block'],
                              ...events.flatMap((e) =>
                                e.logistics.hotel.nights.map((n) => [
                                  fullTitle(e),
                                  n.date,
                                  n.rooms,
                                  n.block,
                                  n.overBlock,
                                ]),
                              ),
                            ]),
                        },
                      ]
                    : undefined
                }
                footnote={
                  <>
                    {nightsEach.length === 1
                      ? `Each meeting had ${nightsEach[0]} hotel night${nightsEach[0] === 1 ? '' : 's'} per traveller.`
                      : nightsEach.length
                        ? `Hotel nights per traveller: ${sideBySide
                            .filter((e) => e.logistics.hotel.nightsPerTraveller !== null)
                            .map((e) => `${eventLabel(e)} ${e.logistics.hotel.nightsPerTraveller}`)
                            .join(' · ')}.`
                        : ''}
                    {multiNight.length
                      ? ` Rooms per night: ${multiNight
                          .map(
                            (e) =>
                              `${eventLabel(e)} ${e.logistics.hotel.nights
                                .map((n) => `${n.rooms}${n.block !== null ? `/${n.block}` : ''}`)
                                .join(', ')}`,
                          )
                          .join(' · ')}.`
                      : ''}
                    {admin ? ' Room-nights and dietary breakdowns are in the CSV.' : ''}
                    {anyRating ? '' : ' No ratings are recorded yet, so that column is hidden.'}
                  </>
                }
              >
                <div className="overflow-x-auto">
                  <table className={`${tTable} min-w-[960px]`}>
                    <thead>
                      <tr className={tHead}>
                        <th className={tTh}>Meeting</th>
                        <th className={`${tTh} ${tNum}`}>Expected</th>
                        <th className={`${tTh} ${tNum}`}>Sessions</th>
                        <th className={`${tTh} ${tNum}`}>Speakers</th>
                        <th className={`${tTh} ${tNum}`}>Hotel travellers</th>
                        <th className={`${tTh} ${tNum}`}>{hasBlock ? 'Peak rooms / block' : 'Peak rooms'}</th>
                        <th className={`${tTh} ${tNum}`}>Parking</th>
                        <th className={`${tTh} ${tNum}`}>Dietary needs</th>
                        <th className={tTh}>Travel</th>
                        {anyRating ? <th className={`${tTh} ${tNum}`}>Rating</th> : null}
                      </tr>
                    </thead>
                    <tbody>
                      {sideBySide.map((e) => {
                        const l = e.logistics;
                        const travel = l.travelModes
                          .filter((t) => t.value !== 'Not recorded')
                          .map((t) => `${t.value} ${fmt(t.count)}`)
                          .join(' · ');
                        const affiliations = (e.program?.speakersByAffiliation ?? [])
                          .slice(0, 3)
                          .map((a) => `${a.value} ${fmt(a.count)}`)
                          .join(' · ');
                        const rating = ratingOf(e);
                        return (
                          <tr key={e.id} className={tRow}>
                            <td className={tTd}>
                              {fullTitle(e)}
                              {e.kind !== 'national' ? (
                                <span className={tEm}>{KIND_LABEL[e.kind] ?? e.kind}</span>
                              ) : null}
                            </td>
                            <td className={`${tTd} ${tNum}`}>{fmt(l.expectedInPerson)}</td>
                            <td className={`${tTd} ${tNum}`}>{e.program ? fmt(e.program.sessions) : dash}</td>
                            <td className={`${tTd} ${tNum}`}>
                              {e.program ? fmt(e.program.speakers) : dash}
                              {affiliations ? <span className={tEm}>{affiliations}</span> : null}
                            </td>
                            <td className={`${tTd} ${tNum}`}>{fmt(l.hotel.travellers)}</td>
                            <td className={`${tTd} ${tNum}`}>
                              {fmt(l.hotel.peakRooms)}
                              {hasBlock ? <> / {l.hotel.block === null ? dash : fmt(l.hotel.block)}</> : null}
                            </td>
                            <td className={`${tTd} ${tNum}`}>{fmt(l.parking)}</td>
                            <td className={`${tTd} ${tNum}`}>{fmt(l.dietary)}</td>
                            <td className={`${tTd} text-ink-2`}>{travel || dash}</td>
                            {anyRating ? <td className={`${tTd} ${tNum}`}>{rating ?? dash}</td> : null}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Block>
            </div>
          ) : null}

          <div className={`${cols} mt-12`}>
            <Block
              title="How many events people come to"
              note={d.notes.retention}
              csv={csv('retention', [
                ['Events attended', 'People', 'Members'],
                ...d.retention.distribution.map((b, i) => [
                  b.value,
                  b.count,
                  d.retention.memberDistribution[i]?.count ?? 0,
                ]),
              ])}
              footnote={`${fmt(d.retention.twoPlus)} of ${fmt(d.retention.people)} people came to two events or more (${fmt(
                d.retention.membersTwoPlus,
              )} of ${fmt(d.retention.members)} members).`}
            >
              <table className={tTable}>
                <thead>
                  <tr className={tHead}>
                    <th className={tTh}>Events attended</th>
                    <th className={`${tTh} ${tNum}`}>People</th>
                    <th className={`${tTh} w-[45%]`}>
                      <span className="sr-only">Bar</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {d.retention.distribution.map((b) => (
                    <tr key={b.value} className={tRow}>
                      <td className={tTd}>{b.value}</td>
                      <td className={`${tTd} ${tNum}`}>{fmt(b.count)}</td>
                      <td className={tTd}>
                        <InlineBar main={b.count} max={retentionMax} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Block>

            <Block
              title="Fellow cohorts at events"
              note={d.notes.cohorts}
              csv={csv('cohorts', [
                ['Cohort', 'Size', 'Year', 'Tenure year', 'Attended', 'Rate'],
                ...d.cohorts.flatMap((c) =>
                  c.years.map((y) => [c.cohort, c.size, y.year, y.tenureYear, y.attended, y.rate]),
                ),
              ])}
            >
              {!d.cohorts.length ? (
                <p className="text-[15px] leading-[22px] text-ink-2">
                  No Fellow cohort had an event in its tenure in this selection.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className={tTable}>
                    <thead>
                      <tr className={tHead}>
                        <th className={tTh}>Cohort</th>
                        <th className={`${tTh} ${tNum}`}>Year 1</th>
                        <th className={`${tTh} ${tNum}`}>Year 2</th>
                        <th className={`${tTh} ${tNum}`}>Year 3</th>
                        <th className={`${tTh} ${tNum}`}>Year 4</th>
                        <th className={`${tTh} ${tNum}`}>Any</th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.cohorts.map((c) => (
                        <tr key={c.cohort} className={tRow}>
                          <td className={tTd}>
                            {c.cohort} <span className="text-ink-3">({fmt(c.size)})</span>
                          </td>
                          {[1, 2, 3, 4].map((t) => {
                            const y = c.years.find((x) => x.tenureYear === t);
                            return (
                              <td key={t} className={`${tTd} ${tNum}`}>
                                {y ? `${rate(y.rate)} (${y.attended})` : dash}
                              </td>
                            );
                          })}
                          <td className={`${tTd} ${tNum}`}>{rate(c.attendedAnyRate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Block>
          </div>

          {yearForWho ? (
            <div className="mt-14 max-w-[720px]">
              <Block
                title="Who comes: profile and equity"
                note={d.notes.who}
                csv={csv(`who-${dimension}-${yearForWho.year}`, [
                  ['Category', 'People'],
                  ...yearForWho.who[dimension].items.map((i) => [i.value, i.count]),
                  [],
                  ['Equity', 'Yes', 'Answered'],
                  ...yearForWho.who.equity.booleans.map((b) => [b.label, b.yes, b.answered]),
                ])}
              >
                <div className="flex flex-wrap gap-3 mb-4">
                  <label className="sr-only" htmlFor="who-dimension">
                    Breakdown
                  </label>
                  <select
                    id="who-dimension"
                    className={filterSelect}
                    value={dimension}
                    onChange={(e) => setDimension(e.target.value as typeof dimension)}
                  >
                    {DIMENSIONS.map(([k, l]) => (
                      <option key={k} value={k}>
                        {l}
                      </option>
                    ))}
                  </select>
                  <label className="sr-only" htmlFor="who-year">
                    Year
                  </label>
                  <select
                    id="who-year"
                    className={filterSelect}
                    value={yearForWho.year}
                    onChange={(e) => setWhoYear(Number(e.target.value))}
                  >
                    {years.map((y) => (
                      <option key={y.year} value={y.year}>
                        {y.year}
                      </option>
                    ))}
                  </select>
                </div>
                {!yearForWho.who[dimension].items.length ? (
                  <p className="text-[15px] leading-[22px] text-ink-2">Nobody yet.</p>
                ) : (
                  <table className={tTable}>
                    <thead>
                      <tr className={tHead}>
                        <th className={tTh}>{DIMENSIONS.find(([k]) => k === dimension)?.[1]}</th>
                        <th className={`${tTh} ${tNum}`}>People</th>
                        <th className={`${tTh} w-[40%]`}>
                          <span className="sr-only">Bar</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {yearForWho.who[dimension].items.map((i) => (
                        <tr key={i.value} className={tRow}>
                          <td className={tTd}>{i.value}</td>
                          <td className={`${tTd} ${tNum}`}>{fmt(i.count)}</td>
                          <td className={tTd}>
                            <InlineBar
                              main={i.count}
                              max={Math.max(1, ...yearForWho.who[dimension].items.map((x) => x.count))}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                <table className={`${tTable} mt-6`}>
                  <thead>
                    <tr className={tHead}>
                      <th className={tTh}>Equity (members who answered)</th>
                      <th className={`${tTh} ${tNum}`}>Yes</th>
                      <th className={`${tTh} ${tNum}`}>Answered</th>
                    </tr>
                  </thead>
                  <tbody>
                    {yearForWho.who.equity.booleans.map((b) => (
                      <tr key={b.key} className={tRow}>
                        <td className={tTd}>{b.label}</td>
                        <td className={`${tTd} ${tNum}`}>{fmt(b.yes)}</td>
                        <td className={`${tTd} ${tNum}`}>{fmt(b.answered)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Block>
            </div>
          ) : null}

          <p className="text-[15px] leading-[22px] text-ink-2 mt-10">
            Also:{' '}
            <button
              type="button"
              aria-expanded={also === 'returning'}
              className="text-accent underline underline-offset-[3px] hover:text-ink"
              onClick={() => setAlso(also === 'returning' ? '' : 'returning')}
            >
              New and returning people per year
            </button>
          </p>

          {also === 'returning' ? (
            <div className="mt-6 max-w-[720px]">
              <Block
                title="New and returning people per year"
                note={d.notes.returning}
                csv={csv('new-returning', [
                  ['Year', 'Different people', 'First time', 'Returning'],
                  ...years.map((y) => [y.year, y.uniqueAttendees, y.firstTime, y.returning]),
                ])}
                footnote="Thick bar: came before. Thin bar: everyone that year."
              >
                <table className={tTable}>
                  <thead>
                    <tr className={tHead}>
                      <th className={tTh}>Year</th>
                      <th className={`${tTh} ${tNum}`}>Different people</th>
                      <th className={`${tTh} ${tNum}`}>First event</th>
                      <th className={`${tTh} ${tNum}`}>Came before</th>
                      <th className={`${tTh} w-[35%]`}>
                        <span className="sr-only">Bar</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {years.map((y) => (
                      <tr key={y.year} className={tRow}>
                        <td className={tTd}>{y.year}</td>
                        <td className={`${tTd} ${tNum}`}>{fmt(y.uniqueAttendees)}</td>
                        <td className={`${tTd} ${tNum}`}>{fmt(y.firstTime)}</td>
                        <td className={`${tTd} ${tNum}`}>{fmt(y.returning)}</td>
                        <td className={tTd}>
                          <InlineBar
                            main={y.returning}
                            compare={y.firstTime + y.returning}
                            max={Math.max(1, ...years.map((x) => x.firstTime + x.returning))}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Block>
            </div>
          ) : null}
        </>
      )}
    </Panel>
  );
}
